import React, { useState } from 'react';
import { CheckIcon, LockIcon, CreditCardIcon } from 'lucide-react';
import { CTAButton } from './CTAButton';
type PackOption = 'basic' | 'premium' | 'economia' | 'contabilidad' | 'organizaciones';
export function PricingSection() {
  const [selectedPack, setSelectedPack] = useState<PackOption>('premium');
  const links: Record<PackOption, string> = {
    basic: 'https://checkout.kitonline.shop/cart/48056006705390:1',
    premium: 'https://checkout.kitonline.shop/cart/48056006410478:1',
    economia: 'https://checkout.kitonline.shop/cart/48056001790190:1',
    contabilidad: 'https://checkout.kitonline.shop/cart/48056007098606:1',
    organizaciones: 'https://checkout.kitonline.shop/cart/48056007557358:1'
  };
  const RadioDot = ({
    active
  }: {
    active: boolean;
  }) => <div className="absolute top-1.5 right-1.5" data-id="element-318">
      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${active ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-319">
        {active && <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} data-id="element-320" />}
      </div>
    </div>;
  return <section id="pricing" className="bg-[#0a0e27] py-4 md:py-6 px-4 font-sans" data-id="element-321">
      <div className="max-w-5xl mx-auto" data-id="element-322">
        <h2 className="text-3xl md:text-5xl font-extrabold text-white text-center italic mb-4" data-id="element-323">
          Elegí tu Pack Completo
        </h2>

        {/* Row 1: Basic + Premium */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-4xl mx-auto mb-3" data-id="element-324">
          {/* Basic - Educación Financiera (amber accent) */}
          <div onClick={() => setSelectedPack('basic')} className={`relative bg-[#111638] rounded-lg p-2 pt-2.5 text-center cursor-pointer transition-all duration-300 border-2 ${selectedPack === 'basic' ? 'border-[#f59e0b] shadow-[0_0_20px_rgba(245,158,11,0.2)]' : 'border-gray-800 hover:border-gray-700'}`} data-id="element-325">
            <RadioDot active={selectedPack === 'basic'} data-id="element-326" />
            <h3 className="text-xl font-bold text-[#f59e0b] italic mb-0" data-id="element-327">
              KIT COMPLETO EDUCACIÓN FINANCIERA
            </h3>
            <p className="text-gray-400 text-xs mb-0.5" data-id="element-328">
              Para Docentes de Secundaria Argentina
            </p>
            <p className="text-gray-500 text-[10px] mb-0.5" data-id="element-329">
              6 Tomos + 4 Bonus
            </p>
            <div className="text-4xl font-black text-white tracking-tight leading-none" data-id="element-330">
              $24.990
            </div>
            <div className="text-gray-500 line-through text-sm font-medium decoration-red-500/50 decoration-2" data-id="element-331">
              $39.990
            </div>
          </div>

          {/* Premium */}
          <div onClick={() => setSelectedPack('premium')} className={`relative bg-[#111638] rounded-lg p-2 pt-2.5 text-center cursor-pointer transition-all duration-300 border-2 ${selectedPack === 'premium' ? 'border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'border-gray-800 hover:border-gray-700'}`} data-id="element-332">
            <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2 bg-[#22c55e] text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full flex items-center gap-1 shadow-lg whitespace-nowrap" data-id="element-333">
              ⭐ Más elegido
            </div>
            <RadioDot active={selectedPack === 'premium'} data-id="element-334" />
            <h3 className="text-xl font-bold text-white italic mb-0" data-id="element-335">
              PACK PREMIUM
            </h3>
            <p className="text-gray-400 text-xs mb-0.5" data-id="element-336">
              Kit Completo + Proyecto Argentina 2026
            </p>
            <p className="text-gray-500 text-[10px] mb-0.5" data-id="element-337">
              6 Tomos + 4 Bonus + Inmersión Total
            </p>
            <div className="text-4xl font-black text-white tracking-tight leading-none" data-id="element-338">
              $29.990
            </div>
            <div className="text-gray-500 line-through text-sm font-medium decoration-red-500/50 decoration-2" data-id="element-339">
              $49.990
            </div>
          </div>
        </div>

        {/* Row 2: Economia + Contabilidad */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-4xl mx-auto mb-3" data-id="element-340">
          {/* Economia */}
          <div onClick={() => setSelectedPack('economia')} className={`relative bg-[#111638] rounded-lg p-2 pt-2.5 text-center cursor-pointer transition-all duration-300 border-2 ${selectedPack === 'economia' ? 'border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'border-gray-800 hover:border-gray-700'}`} data-id="element-341">
            <RadioDot active={selectedPack === 'economia'} data-id="element-342" />
            <h3 className="text-lg font-bold text-white italic mb-0 leading-tight" data-id="element-343">
              KIT EDUCACIÓN FINANCIERA + ECONOMÍA LISTA
            </h3>
            <p className="text-gray-500 text-[10px] mb-0.5" data-id="element-344">
              10 Tomos + 7 Bonus
            </p>
            <div className="text-4xl font-black text-white tracking-tight leading-none" data-id="element-345">
              $37.990
            </div>
          </div>

          {/* Contabilidad */}
          <div onClick={() => setSelectedPack('contabilidad')} className={`relative bg-[#111638] rounded-lg p-2 pt-2.5 text-center cursor-pointer transition-all duration-300 border-2 ${selectedPack === 'contabilidad' ? 'border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'border-gray-800 hover:border-gray-700'}`} data-id="element-346">
            <RadioDot active={selectedPack === 'contabilidad'} data-id="element-347" />
            <h3 className="text-lg font-bold text-white italic mb-0 leading-tight" data-id="element-348">
              KIT EDUCACIÓN FINANCIERA + CONTABILIDAD LISTA
            </h3>
            <p className="text-gray-500 text-[10px] mb-0.5" data-id="element-349">
              10 Tomos + 7 Bonus
            </p>
            <div className="text-4xl font-black text-white tracking-tight leading-none" data-id="element-350">
              $37.990
            </div>
          </div>
        </div>

        {/* Row 3: Organizaciones */}
        <div className="max-w-4xl mx-auto mb-4" data-id="element-351">
          <div onClick={() => setSelectedPack('organizaciones')} className={`relative bg-[#111638] rounded-lg p-2 pt-2.5 text-center cursor-pointer transition-all duration-300 border-2 ${selectedPack === 'organizaciones' ? 'border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'border-gray-800 hover:border-gray-700'} max-w-sm mx-auto md:max-w-none`} data-id="element-352">
            <RadioDot active={selectedPack === 'organizaciones'} data-id="element-353" />
            <h3 className="text-lg font-bold text-white italic mb-0 leading-tight" data-id="element-354">
              KIT EDUCACIÓN FINANCIERA + TEORÍA DE LAS ORGANIZACIONES
            </h3>
            <p className="text-gray-500 text-[10px] mb-0.5" data-id="element-355">
              12 Tomos + 7 Bonus
            </p>
            <div className="text-4xl font-black text-white tracking-tight leading-none" data-id="element-356">
              $37.990
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mb-4" data-id="element-357">
          <CTAButton href={links[selectedPack]} className="w-full max-w-md py-3 text-xl italic shadow-[0_0_20px_rgba(34,197,94,0.3)] mb-1.5" data-id="element-358">
            Sí, quiero mi pack →
          </CTAButton>
          <div className="flex items-center justify-center gap-2 text-gray-500 text-xs font-medium uppercase tracking-wider" data-id="element-359">
            <LockIcon className="w-3 h-3" data-id="element-360" /> Pago seguro con Mercado Pago
          </div>
        </div>

        {/* Pack Includes */}
        <div className="max-w-2xl mx-auto bg-[#111638] border border-gray-800 rounded-lg p-3 md:p-4 mb-3 shadow-xl" data-id="element-361">
          <h4 className="text-white font-bold text-center mb-2 uppercase tracking-wide flex items-center justify-center gap-2" data-id="element-362">
            👑 PACK COMPLETO INCLUYE:
          </h4>
          <ul className="space-y-1.5" data-id="element-363">
            <li className="flex items-start gap-2" data-id="element-364">
              <div className="bg-[#22c55e] rounded flex-shrink-0 mt-0.5 w-4 h-4 flex items-center justify-center" data-id="element-365">
                <CheckIcon className="w-3 h-3 text-white" strokeWidth={4} data-id="element-366" />
              </div>
              <span className="text-gray-300 text-sm md:text-base" data-id="element-367">
                Kit completo de Educación Financiera para secundaria
              </span>
            </li>
            <li className="flex items-start gap-2" data-id="element-368">
              <div className="bg-[#22c55e] rounded flex-shrink-0 mt-0.5 w-4 h-4 flex items-center justify-center" data-id="element-369">
                <CheckIcon className="w-3 h-3 text-white" strokeWidth={4} data-id="element-370" />
              </div>
              <span className="text-gray-300 text-sm md:text-base" data-id="element-371">
                10 tomos con secuencias didácticas completas
              </span>
            </li>
            <li className="flex items-start gap-2" data-id="element-372">
              <div className="bg-[#22c55e] rounded flex-shrink-0 mt-0.5 w-4 h-4 flex items-center justify-center" data-id="element-373">
                <CheckIcon className="w-3 h-3 text-white" strokeWidth={4} data-id="element-374" />
              </div>
              <span className="text-gray-300 text-sm md:text-base" data-id="element-375">
                7 BONUS incluidos
              </span>
            </li>
            <li className="flex items-start gap-2" data-id="element-376">
              <div className="bg-[#22c55e] rounded flex-shrink-0 mt-0.5 w-4 h-4 flex items-center justify-center" data-id="element-377">
                <CheckIcon className="w-3 h-3 text-white" strokeWidth={4} data-id="element-378" />
              </div>
              <span className="text-gray-300 text-sm md:text-base" data-id="element-379">
                Entrega inmediata (PDF)
              </span>
            </li>
            <li className="flex items-start gap-2" data-id="element-380">
              <div className="bg-[#22c55e] rounded flex-shrink-0 mt-0.5 w-4 h-4 flex items-center justify-center" data-id="element-381">
                <CheckIcon className="w-3 h-3 text-white" strokeWidth={4} data-id="element-382" />
              </div>
              <span className="text-gray-300 text-sm md:text-base" data-id="element-383">
                Acceso de por vida
              </span>
            </li>
            <li className="flex items-start gap-2" data-id="element-384">
              <div className="bg-[#22c55e] rounded flex-shrink-0 mt-0.5 w-4 h-4 flex items-center justify-center" data-id="element-385">
                <CheckIcon className="w-3 h-3 text-white" strokeWidth={4} data-id="element-386" />
              </div>
              <span className="text-gray-300 text-sm md:text-base" data-id="element-387">
                Garantía de satisfacción de 7 días
              </span>
            </li>
            <li className="flex items-start gap-2" data-id="element-388">
              <div className="bg-[#22c55e] rounded flex-shrink-0 mt-0.5 w-4 h-4 flex items-center justify-center" data-id="element-389">
                <CheckIcon className="w-3 h-3 text-white" strokeWidth={4} data-id="element-390" />
              </div>
              <span className="text-gray-300 text-sm md:text-base" data-id="element-391">
                Alineado NAP 2026
              </span>
            </li>
          </ul>
        </div>

        {/* Payment Methods */}
        <div className="flex justify-center" data-id="element-392">
          <div className="inline-flex items-center gap-2 border border-gray-800 rounded-full px-4 py-1.5 text-gray-500 text-xs md:text-sm" data-id="element-393">
            <CreditCardIcon className="w-3.5 h-3.5" data-id="element-394" />
            <span data-id="element-395">Transferencia · Mercado Pago · Tarjeta</span>
          </div>
        </div>
      </div>
    </section>;
}